import { CircularProgress } from '@mui/material';
import { t } from 'i18next';
import Link from 'next/link';
import router from 'next/router';
import React, { useRef, useState } from 'react';
import LockIcon from '../../../../assets/lock.svg';
import Logo from '../../../../assets/logo-novo-cointimes.svg';
import { useForgotPassword } from '../../../../hooks/forgotPassword';
import { FormProvider, useForm } from '../../../../hooks/form';
import { Button, Input } from '../../../UI';

interface StepProps {
  formStep?: number;
  nextFormStep: () => void;
  prevFormStep: () => void;
  setErrorCode: (value: string) => void;
}

const PassField: React.FC<StepProps> = ({
  formStep,
  prevFormStep,
  setErrorCode,
}) => {
  const [signupError, setSignupError] = useState('');
  const { data, putPassword } = useForgotPassword();
  const [loading, setLoading] = useState(false);

  const methods = useForm();

  const {
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const new_password = useRef({});
  new_password.current = watch('new_password', '');

  const onSubmit = async values => {
    try {
      setLoading(true);
      const allData = {
        ...data,
        ...values,
      };
      delete allData['password_repeat'];

      await putPassword(allData);
      router.push('/login');
    } catch (err) {
      setLoading(false);
      console.log(`request failed: ${err}`);
      setErrorCode(t('pages.forgot-password.invalid-code'));
      prevFormStep();
    }
  };

  return (
    <div className="rightContent">
      <Link href="/" passHref>
        <div className="logo">
          <Logo height={60} />
        </div>
      </Link>
      <span className="subTitle">{t('pages.forgot-password.create-new-password-message')}</span>
      <FormProvider {...methods}>
        <form className="register passField" onSubmit={handleSubmit(onSubmit)}>
          <div className="alignInput">
            <Input.Default
              icon={<LockIcon />}
              name="new_password"
              type="password"
              placeholder={t('pages.forgot-password.new-password-input')}
              registerField={{
                name: 'new_password',
                options: {
                  required: t('pages.forgot-password.new-password-input-required'),
                  minLength: {
                    value: 6,
                    message: t('pages.forgot-password.new-password-input-min-six-char'),
                  },
                },
              }}
            />
          </div>
          <div className="alignInput">
            <Input.Default
              icon={<LockIcon />}
              name="password_repeat"
              type="password"
              placeholder={t('pages.forgot-password.confirm-new-password-input')}
              registerField={{
                name: 'password_repeat',
                options: {
                  required: t('pages.forgot-password.confirm-new-password-input-required'),
                  validate: value =>
                    value === new_password.current || t('pages.forgot-password.password-input-mismatch'),
                },
              }}
            />
          </div>
          {errors.name ? (
            <span className="error">{errors.name.message}</span>
          ) : errors.new_password ? (
            <span className="error">{errors.new_password.message}</span>
          ) : errors.password_repeat ? (
            <span className="error">{errors.password_repeat.message}</span>
          ) : undefined}
          <Button className="formButton" type="submit">
            {loading ? (
              <CircularProgress size={24} className="loading" />
            ) : (
              t('pages.forgot-password.change-password-button')
            )}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
};

export default PassField;
